<template>
  <div class="creq">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('requests.myTitle') }}</h1>
        <p class="page-sub">{{ t('requests.mySub') }}</p>
      </div>
      <button class="btn btn-primary" @click="openForm(null)">+ {{ t('requests.newBtn') }}</button>
    </div>

    <div v-if="showForm" class="req-form">
      <div class="seg" :class="{ disabled: !!editingId }">
        <button type="button" :class="{ active: form.type === 'project' }" :disabled="!!editingId" @click="form.type = 'project'">{{ t('requests.typeProject') }}</button>
        <button type="button" :class="{ active: form.type === 'task' }" :disabled="!!editingId" @click="form.type = 'task'">{{ t('requests.typeTask') }}</button>
      </div>
      <div class="form-group">
        <label>{{ t('requests.fieldTitle') }} *</label>
        <input v-model="form.title" class="form-control" maxlength="200" :placeholder="t('requests.titlePh')" />
      </div>
      <div class="form-group">
        <label>{{ t('requests.fieldDesc') }}</label>
        <textarea v-model="form.description" class="form-control" rows="3" :placeholder="t('requests.descPh')"></textarea>
      </div>
      <div v-if="form.type === 'task'" class="form-group">
        <label>{{ t('requests.fieldProject') }} *</label>
        <select v-model="form.project_id" class="form-control" :disabled="!!editingId">
          <option :value="null">{{ t('requests.selectProject') }}</option>
          <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
      </div>
      <div class="form-group">
        <label>{{ t('requests.deadline') }}</label>
        <input v-model="form.desired_deadline" type="date" class="form-control" />
      </div>
      <div v-if="form.type === 'project'" class="form-group">
        <label>{{ t('requests.billingPref') }}</label>
        <select v-model="form.desired_pricing_type" class="form-control">
          <option value="none">{{ t('projects.pricingNone') }}</option>
          <option value="fixed">{{ t('projects.pricingFixed') }}</option>
          <option value="hourly">{{ t('projects.pricingHourly') }}</option>
        </select>
        <input v-if="form.desired_pricing_type !== 'none'" v-model="form.desired_price_amount" type="number" min="0" step="0.01" class="form-control amt" :placeholder="t('requests.amount')" />
      </div>
      <p v-if="error" class="form-error">{{ error }}</p>
      <div class="modal-actions">
        <button type="button" class="btn btn-ghost" @click="showForm = false">{{ t('common.cancel') }}</button>
        <button type="button" class="btn btn-primary" :disabled="!canSubmit || saving" @click="submit">
          {{ saving ? t('common.saving') : (editingId ? t('requests.sendCounter') : t('requests.submit')) }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="spinner"></div>
    <div v-else-if="requests.length === 0" class="empty-state"><p>{{ t('requests.empty') }}</p></div>
    <div v-else class="req-list">
      <div v-for="r in requests" :key="r.id" class="req-item" :class="{ revised: r.status === 'revised' }">
        <div class="req-row">
          <div class="req-main">
            <div class="req-title">{{ r.title }}</div>
            <div class="req-meta">
              {{ r.type === 'project' ? t('requests.typeProject') : t('requests.typeTask') }}
              <template v-if="r.desired_deadline"> · {{ t('requests.deadline') }} {{ fmtDate(r.desired_deadline) }}</template>
              <template v-if="r.desired_pricing_type !== 'none'"> · {{ pricingLabel(r) }}</template>
            </div>
          </div>
          <span :class="['req-status', 'st-' + r.status]">{{ t('requests.' + r.status) }}</span>
        </div>
        <!-- Negotiation: team proposed changes -->
        <div v-if="r.status === 'revised'" class="req-revised">
          <p class="rv-note">{{ t('requests.teamRevised') }}</p>
          <div class="rv-actions">
            <button class="btn btn-sm btn-ghost" @click="openForm(r)">{{ t('requests.counter') }}</button>
            <button class="btn btn-sm btn-primary" :disabled="busy === r.id" @click="accept(r)">{{ t('requests.acceptRevision') }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'
import { onRealtime, debounce } from '@/composables/useRealtime'
import { fmtMoney } from '@/utils/billing'

const blank = () => ({ type: 'project', title: '', description: '', project_id: null, desired_pricing_type: 'none', desired_price_amount: '', desired_deadline: '' })

export default {
  name: 'ClientRequests',
  setup() {
    const { t, locale } = useI18n()
    const requests = ref([])
    const projects = ref([])
    const loading = ref(true)
    const showForm = ref(false)
    const saving = ref(false)
    const busy = ref(null)
    const error = ref('')
    const editingId = ref(null)
    const form = reactive(blank())

    const canSubmit = computed(() => form.title.trim() && (form.type !== 'task' || form.project_id))

    function fmtDate(dt) {
      if (!dt) return ''
      return new Date(dt + 'T00:00:00').toLocaleDateString(locale.value === 'he' ? 'he-IL' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }
    const pricingLabel = (r) => {
      const lbl = r.desired_pricing_type === 'fixed' ? t('projects.pricingFixed') : t('projects.pricingHourly')
      return r.desired_price_amount != null ? `${lbl} ${fmtMoney(r.desired_price_amount)}` : lbl
    }

    function openForm(r) {
      error.value = ''
      editingId.value = r ? r.id : null
      Object.assign(form, r ? {
        type: r.type, title: r.title, description: r.description || '', project_id: r.project_id,
        desired_pricing_type: r.desired_pricing_type, desired_price_amount: r.desired_price_amount ?? '',
        desired_deadline: r.desired_deadline || '',
      } : blank())
      showForm.value = true
    }

    async function load() {
      loading.value = true
      try {
        const [rr, pr] = await Promise.all([api.getRequests(), api.getProjects()])
        requests.value = rr.data || []
        projects.value = pr.data || []
      } catch {} finally { loading.value = false }
    }

    async function submit() {
      error.value = ''
      saving.value = true
      try {
        const payload = {
          title: form.title,
          description: form.description || null,
          desired_deadline: form.desired_deadline || null,
          desired_pricing_type: form.type === 'project' ? form.desired_pricing_type : 'none',
          desired_price_amount: form.type === 'project' && form.desired_pricing_type !== 'none' && form.desired_price_amount !== '' ? Number(form.desired_price_amount) : null,
        }
        if (editingId.value) {
          await api.updateRequest(editingId.value, payload)
        } else {
          await api.createRequest({ ...payload, type: form.type, project_id: form.type === 'task' ? form.project_id : null })
        }
        showForm.value = false
        await load()
      } catch (e) {
        const errs = e.data && e.data.errors
        error.value = errs ? Object.values(errs).flat().join(' ') : (e.message || 'Failed')
      } finally { saving.value = false }
    }

    async function accept(r) {
      busy.value = r.id
      try { await api.acceptRequest(r.id); await load() } catch {} finally { busy.value = null }
    }

    let off = () => {}
    onMounted(() => { load(); off = onRealtime('request.changed', debounce(load, 400)) })
    onUnmounted(() => off())

    return { t, requests, projects, loading, showForm, saving, busy, error, form, editingId, canSubmit, openForm, submit, accept, fmtDate, pricingLabel }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.req-form { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 20px; margin-bottom: 22px; max-width: 540px; }
.seg { display: inline-flex; border: 1px solid var(--border); border-radius: 10px; overflow: hidden; margin-bottom: 16px; }
.seg.disabled { opacity: .6; }
.seg button { border: none; background: var(--surface); color: var(--muted); padding: 8px 18px; font: inherit; font-size: 13px; cursor: pointer; }
.seg button.active { background: var(--accent); color: var(--on-accent); font-weight: 600; }
.amt { margin-top: 8px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }

.req-list { display: flex; flex-direction: column; gap: 8px; }
.req-item { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 14px 16px; }
.req-item.revised { border-color: var(--accent); }
.req-row { display: flex; align-items: center; gap: 12px; }
.req-main { flex: 1; min-width: 0; }
.req-title { font-size: 14px; font-weight: 500; }
.req-meta { font-size: 12px; color: var(--muted); margin-top: 2px; }
.req-status { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 999px; white-space: nowrap; }
.st-pending  { background: var(--accent-wash); color: var(--accent); }
.st-revised  { background: rgba(180,117,20,.14); color: var(--high); }
.st-approved { background: rgba(30,138,83,.12); color: var(--success); }
.st-declined { background: rgba(209,68,68,.12); color: var(--danger); }
.req-revised { margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border); }
.rv-note { font-size: 13px; color: var(--text); margin-bottom: 10px; }
.rv-actions { display: flex; justify-content: flex-end; gap: 8px; }
</style>
